import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { assistentes as apiAssistentes, type AssistenteNaLista } from '../api/assistentes'
import { ErroApi } from '../api/client'
import {
  conhecimento as api,
  type Documento,
  type DocumentoNaLista,
  type ResultadoDaBusca,
  type ResultadoDaPublicacao,
} from '../api/conhecimento'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import { formatar } from '../i18n'
import { mensagemDeErro } from './mensagemDeErro'
import './usuarios.css'
import './conhecimento.css'
import { marcaDaRota } from './marcaDaRota'


export function Conhecimento() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const marca = (marcaDaRota() ?? '')

  const [lista, setLista] = useState<DocumentoNaLista[] | null>(null)
  const [assistentes, setAssistentes] = useState<AssistenteNaLista[]>([])
  const [assistente, setAssistente] = useState<number | null>(null)
  const [falhou, setFalhou] = useState(false)
  const [editando, setEditando] = useState<number | 'novo' | null>(null)
  const [recado, setRecado] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      const [documentos, quais] = await Promise.all([
        api.listar(marca, assistente ?? undefined),
        apiAssistentes.listar(marca),
      ])

      setLista(documentos)
      setAssistentes(quais)
    } catch {
      setFalhou(true)
    }
  }, [marca, assistente])

  useEffect(() => {
    void carregar()
  }, [carregar])

  if (falhou)
    return <Estado tipo="erro" acao={<Botao onClick={carregar}>{t('comum.tentarDeNovo')}</Botao>} />

  if (!lista) return <Estado tipo="carregando" />

  if (editando !== null)
    return (
      <Formulario
        marca={marca}
        id={editando === 'novo' ? null : editando}
        assistentes={assistentes}
        aoFechar={() => setEditando(null)}
        aoSalvar={async () => {
          setEditando(null)
          await carregar()
        }}
      />
    )

  const publicados = lista.filter((d) => d.situacao === 'Publicado').length

  return (
    <main className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('conhecimento.titulo')}</h1>
          <p className="usuarios__descricao">{t('conhecimento.descricao', { marca })}</p>
        </div>

        {pode('ParametroGerenciar') && (
          <Botao variante="primario" onClick={() => setEditando('novo')}>
            {t('conhecimento.novo')}
          </Botao>
        )}
      </header>

      {recado && <Aviso tom="sucesso">{recado}</Aviso>}

      {/* 🔴 O aviso que mais importa nesta tela. Enquanto não houver documento
          publicado, o assistente conversa bem e, sobre catálogo, responde que
          vai verificar — e quem vê isso numa demonstração conclui que a IA não
          funciona, quando o que falta é conteúdo. */}
      {publicados === 0 && (
        <Aviso tom="alerta">{t('conhecimento.nadaPublicado')}</Aviso>
      )}

      <Cartao titulo={t('conhecimento.importar')}>
        <Importacao
          marca={marca}
          assistentes={assistentes}
          podeGerenciar={pode('ParametroGerenciar')}
          aoImportar={async (recadoDaImportacao) => {
            setRecado(recadoDaImportacao)
            await carregar()
          }}
        />
      </Cartao>

      <Busca marca={marca} assistentes={assistentes} />

      <Cartao titulo={t('conhecimento.documentos')}>
        <label className="usuarios__filtro">
          <span>{t('conhecimento.assistente')}</span>
          <select
            value={assistente ?? ''}
            onChange={(e) => setAssistente(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">{t('conhecimento.todos')}</option>
            {assistentes.map((a) => (
              <option key={a.id} value={a.id}>
                {a.nome}
              </option>
            ))}
          </select>
        </label>

        {lista.length === 0 ? (
          <Estado tipo="vazio" descricao={t('conhecimento.vazio')} />
        ) : (
          <div
              className="usuarios__tabela-rolagem"
              // A região rolável precisa ser alcançável pelo teclado (WCAG 2.1.1):
              // sem isto, a parte escondida da tabela não existe para quem não usa mouse.
              tabIndex={0}
              role="group"
              aria-label={t('conhecimento.legenda')}
            >
              <table className="usuarios__tabela">
              <caption className="usuarios__legenda">{t('conhecimento.legenda')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('conhecimento.tituloDoDocumento')}</th>
                  <th scope="col">{t('conhecimento.assistente')}</th>
                  <th scope="col">{t('conhecimento.origem')}</th>
                  <th scope="col">{t('conhecimento.trechos')}</th>
                  <th scope="col">{t('usuarios.situacao')}</th>
                  <th scope="col">{t('usuarios.acoes')}</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((d) => (
                  <tr key={d.id}>
                    <td>{d.titulo}</td>
                    <td>{d.assistente}</td>
                    <td>{d.origem}</td>
                    <td>{d.trechos === 0 ? '—' : formatar.numero(d.trechos)}</td>
                    <td>
                      <span className={`etiqueta etiqueta--${etiqueta(d.situacao)}`}>
                        {t(`conhecimento.situacao${d.situacao}`)}
                      </span>
                    </td>
                    <td>
                      <Botao variante="discreto" onClick={() => setEditando(d.id)}>
                        {pode('ParametroGerenciar') ? t('usuarios.editar') : t('assistentes.ver')}
                      </Botao>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Cartao>
    </main>
  )
}

function etiqueta(situacao: DocumentoNaLista['situacao']) {
  if (situacao === 'Publicado') return 'ativo'
  if (situacao === 'Arquivado') return 'inativo'
  return 'pendente'
}

/**
 * Pré-visualização da busca.
 *
 * 🔴 Não é ferramenta de depuração: é o que permite conferir o que o assistente
 * encontraria **antes** de ele responder a um cliente. Se "couché 150g" não traz
 * o trecho certo, o problema é do documento — e isso precisa ser visível na hora
 * de escrevê-lo, não depois, numa conversa.
 */
function Busca({ marca, assistentes }: { marca: string; assistentes: AssistenteNaLista[] }) {
  const { t } = useTranslation()

  const [assistente, setAssistente] = useState<number | null>(null)
  const [pergunta, setPergunta] = useState('')
  const [resultado, setResultado] = useState<ResultadoDaBusca | null>(null)
  const [buscando, setBuscando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const alvo = assistente ?? assistentes[0]?.id ?? null

  async function buscar(evento: FormEvent) {
    evento.preventDefault()
    if (!alvo || !pergunta.trim()) return

    setErro(null)
    setBuscando(true)

    try {
      setResultado(await api.buscar(marca, alvo, pergunta))
    } catch (e) {
      setErro(mensagemDeErro(e, t))
      setResultado(null)
    } finally {
      setBuscando(false)
    }
  }

  return (
    <Cartao titulo={t('conhecimento.buscaTitulo')}>
      <p className="usuarios__descricao">{t('conhecimento.buscaDescricao')}</p>

      <form onSubmit={buscar} className="conhecimento__busca" noValidate>
        <label className="usuarios__filtro">
          <span>{t('conhecimento.assistente')}</span>
          <select
            value={alvo ?? ''}
            onChange={(e) => setAssistente(Number(e.target.value))}
          >
            {assistentes.map((a) => (
              <option key={a.id} value={a.id}>
                {a.nome}
              </option>
            ))}
          </select>
        </label>

        <Campo
          rotulo={t('conhecimento.pergunta')}
          value={pergunta}
          onChange={(e) => setPergunta(e.target.value)}
          ajuda={t('conhecimento.perguntaAjuda')}
        />

        <Botao type="submit" carregando={buscando} disabled={!alvo}>
          {t('comum.buscar')}
        </Botao>
      </form>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}

      {resultado &&
        (resultado.total === 0 ? (
          <Estado tipo="vazio" descricao={t('conhecimento.buscaVazia')} />
        ) : (
          <ol className="conhecimento__achados">
            {resultado.trechos.map((trecho) => (
              <li key={trecho.trechoId} className="conhecimento__achado">
                <p className="conhecimento__achado-titulo">{trecho.titulo}</p>
                <p className="conhecimento__achado-texto">{trecho.texto}</p>

                {/* As posições dizem QUAL perna encontrou. Perna lexical vazia
                    é vocabulário faltando no documento; perna vetorial vazia
                    costuma ser embedding indisponível — respostas diferentes. */}
                <p className="conhecimento__achado-pernas">
                  {trecho.posicaoLexical > 0
                    ? t('conhecimento.lexicalEm', { posicao: trecho.posicaoLexical })
                    : t('conhecimento.lexicalNao')}
                  {' · '}
                  {trecho.posicaoVetorial > 0
                    ? t('conhecimento.vetorialEm', { posicao: trecho.posicaoVetorial })
                    : t('conhecimento.vetorialNao')}
                </p>
              </li>
            ))}
          </ol>
        ))}
    </Cartao>
  )
}

function Importacao({
  marca,
  assistentes,
  podeGerenciar,
  aoImportar,
}: {
  marca: string
  assistentes: AssistenteNaLista[]
  podeGerenciar: boolean
  aoImportar: (recado: string) => Promise<void>
}) {
  const { t } = useTranslation()

  const entrada = useRef<HTMLInputElement>(null)
  const [assistente, setAssistente] = useState<number | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [titulo, setTitulo] = useState('')

  const alvo = assistente ?? assistentes[0]?.id ?? null

  async function importar(evento: FormEvent) {
    evento.preventDefault()

    const arquivo = entrada.current?.files?.[0]
    if (!arquivo || !alvo) return

    setErro(null)
    setEnviando(true)

    try {
      // 🔴 O PDF tem porta própria (050): vira **um** documento com o arquivo
      // guardado, enquanto o markdown vira um por seção. Quem decide é o tipo
      // do arquivo escolhido, e a tela diz o que vai acontecer em cada caso.
      const ehPdf = arquivo.type === 'application/pdf'
        || arquivo.name.toLowerCase().endsWith('.pdf')

      if (ehPdf) {
        const pdf = await api.importarPdf(marca, alvo, arquivo, titulo.trim())

        await aoImportar(
          t('conhecimento.pdfImportado', {
            titulo: pdf.titulo,
            paginas: pdf.paginas,
            caracteres: pdf.caracteres,
          }),
        )
      } else {
        const resultado = await api.importar(marca, alvo, arquivo)

        await aoImportar(
          t('conhecimento.importados', {
            criados: resultado.criados,
            secoes: resultado.secoes,
          }) + (resultado.aviso ? ` ${resultado.aviso}` : ''),
        )
      }

      setTitulo('')
      if (entrada.current) entrada.current.value = ''
    } catch (e) {
      setErro(mensagemDeErro(e, t))
    } finally {
      setEnviando(false)
    }
  }

  if (!podeGerenciar) return <Aviso tom="info">{t('assistentes.somenteLeitura')}</Aviso>

  return (
    <form onSubmit={importar} noValidate>
      <p className="usuarios__descricao">{t('conhecimento.importarDescricao')}</p>

      <label className="usuarios__filtro">
        <span>{t('conhecimento.assistente')}</span>
        <select value={alvo ?? ''} onChange={(e) => setAssistente(Number(e.target.value))}>
          {assistentes.map((a) => (
            <option key={a.id} value={a.id}>
              {a.nome}
            </option>
          ))}
        </select>
      </label>

      <label className="usuarios__filtro">
        <span>{t('conhecimento.arquivo')}</span>
        <input ref={entrada} type="file" accept=".md,text/markdown,.pdf,application/pdf" required />
      </label>

      <Campo
        rotulo={t('conhecimento.tituloDoPdf')}
        ajuda={t('conhecimento.tituloDoPdfDica')}
        value={titulo}
        onChange={(e) => setTitulo(e.target.value)}
      />

      {/* O preço da extração local, dito antes de o arquivo subir. */}
      <Aviso tom="info">{t('conhecimento.pdfDigitalizado')}</Aviso>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}

      {/* Nada é publicado na importação: os documentos entram como rascunho,
          para alguém ler antes. Carga automática direto no índice é como
          conteúdo errado chega ao cliente sem passar por ninguém. */}
      <Aviso tom="info">{t('conhecimento.importaComoRascunho')}</Aviso>

      <Botao type="submit" carregando={enviando} disabled={!alvo}>
        {t('conhecimento.importar')}
      </Botao>
    </form>
  )
}

function Formulario({
  marca,
  id,
  assistentes,
  aoFechar,
  aoSalvar,
}: {
  marca: string
  id: number | null
  assistentes: AssistenteNaLista[]
  aoFechar: () => void
  aoSalvar: () => Promise<void>
}) {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const editavel = pode('ParametroGerenciar')

  const [dados, setDados] = useState<Documento | null>(
    id === null
      ? {
          id: 0,
          assistenteId: assistentes[0]?.id ?? 0,
          titulo: '',
          conteudo: '',
          situacao: 'Rascunho',
          origem: '',
          referencia: null,
          revisadoEm: null,
          revisadoPor: null,
          criadoEm: new Date().toISOString(),
          atualizadoEm: null,
          trechos: [],
        }
      : null,
  )

  const [falhou, setFalhou] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [publicacao, setPublicacao] = useState<ResultadoDaPublicacao | null>(null)
  const [precisaRepublicar, setPrecisaRepublicar] = useState(false)

  useEffect(() => {
    if (id === null) return

    api
      .ler(marca, id)
      .then(setDados)
      .catch(() => setFalhou(true))
  }, [marca, id])

  if (falhou) return <Estado tipo="erro" />
  if (!dados) return <Estado tipo="carregando" />

  const alterar = <C extends keyof Documento>(campo: C, valor: Documento[C]) =>
    setDados({ ...dados, [campo]: valor })

  const pedido = {
    assistenteId: dados.assistenteId,
    titulo: dados.titulo,
    conteudo: dados.conteudo,
    origem: dados.origem,
    referencia: dados.referencia,
    publicarAgora: false,
  }

  async function salvar(evento: FormEvent) {
    evento.preventDefault()
    if (!dados) return

    setErro(null)
    setEnviando(true)

    try {
      if (id === null) {
        await api.criar(marca, pedido)
        await aoSalvar()
      } else {
        const resultado = await api.salvar(marca, id, pedido)
        setPrecisaRepublicar(resultado.precisaRepublicar)
      }
    } catch (e) {
      setErro(mensagemDeErro(e, t))
    } finally {
      setEnviando(false)
    }
  }

  async function publicar() {
    if (id === null) return

    setErro(null)
    setEnviando(true)

    try {
      const resultado = await api.publicar(marca, id)
      setPublicacao(resultado)
      setPrecisaRepublicar(false)
      setDados(await api.ler(marca, id))
    } catch (e) {
      const corpo = e instanceof ErroApi ? (e.corpo as { detail?: string } | null) : null
      setErro(corpo?.detail ?? mensagemDeErro(e, t))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <main className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">
            {id === null ? t('conhecimento.novo') : dados.titulo}
          </h1>
          <p className="usuarios__descricao">{t('conhecimento.formularioDescricao')}</p>
        </div>

        <Botao variante="discreto" onClick={aoFechar}>
          {t('assistentes.voltar')}
        </Botao>
      </header>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}
      {!editavel && <Aviso tom="info">{t('assistentes.somenteLeitura')}</Aviso>}

      {/* Editar não republica sozinho: publicar gasta embedding e muda o que o
          assistente responde, então é ato deliberado. Mas o usuário precisa
          saber que o índice ficou para trás. */}
      {precisaRepublicar && <Aviso tom="alerta">{t('conhecimento.precisaRepublicar')}</Aviso>}

      {publicacao && (
        <Aviso tom={publicacao.reaproveitado ? 'info' : 'sucesso'}>
          {publicacao.reaproveitado
            ? t('conhecimento.semMudanca')
            : t('conhecimento.publicado', { trechos: publicacao.trechos })}
          {publicacao.piiMascarada > 0 &&
            ` ${t('conhecimento.piiMascarada', { total: publicacao.piiMascarada })}`}
        </Aviso>
      )}

      <div className="conhecimento__colunas">
        <Cartao titulo={t('conhecimento.documento')}>
          <form onSubmit={salvar} noValidate>
            <label className="usuarios__filtro">
              <span>{t('conhecimento.assistente')}</span>
              <select
                value={dados.assistenteId}
                disabled={!editavel || id !== null}
                onChange={(e) => alterar('assistenteId', Number(e.target.value))}
              >
                {assistentes.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.nome}
                  </option>
                ))}
              </select>
            </label>

            <Campo
              rotulo={t('conhecimento.tituloDoDocumento')}
              value={dados.titulo}
              onChange={(e) => alterar('titulo', e.target.value)}
              disabled={!editavel}
              obrigatorio
            />

            <Campo
              rotulo={t('conhecimento.origem')}
              value={dados.origem}
              onChange={(e) => alterar('origem', e.target.value)}
              disabled={!editavel}
              ajuda={t('conhecimento.origemAjuda')}
              obrigatorio
            />

            <Campo
              rotulo={t('conhecimento.referencia')}
              value={dados.referencia ?? ''}
              onChange={(e) => alterar('referencia', e.target.value)}
              disabled={!editavel}
            />

            <label className="conhecimento__campo-longo">
              <span>{t('conhecimento.conteudo')}</span>
              <textarea
                value={dados.conteudo}
                disabled={!editavel}
                rows={16}
                onChange={(e) => alterar('conteudo', e.target.value)}
              />
            </label>

            {editavel && (
              <div className="usuarios__acoes-formulario">
                <Botao type="submit" variante="primario" carregando={enviando}>
                  {t('comum.salvar')}
                </Botao>

                {id !== null && (
                  <Botao onClick={publicar} carregando={enviando}>
                    {t('conhecimento.publicar')}
                  </Botao>
                )}
              </div>
            )}
          </form>
        </Cartao>

        <Cartao titulo={t('conhecimento.comoFicouIndexado')}>
          <p className="usuarios__descricao">{t('conhecimento.indexadoDescricao')}</p>

          {dados.trechos.length === 0 ? (
            <Estado tipo="vazio" descricao={t('conhecimento.semTrechos')} />
          ) : (
            <ol className="conhecimento__trechos">
              {dados.trechos.map((trecho) => (
                <li key={trecho.id} className="conhecimento__trecho">
                  <p className="conhecimento__trecho-cabecalho">
                    {t('conhecimento.trecho', { ordem: trecho.ordem + 1 })} ·{' '}
                    {trecho.modeloDoVetor} · {formatar.dataHora(trecho.indexadoEm)}
                  </p>
                  <p className="conhecimento__trecho-texto">{trecho.texto}</p>
                </li>
              ))}
            </ol>
          )}
        </Cartao>
      </div>
    </main>
  )
}
